/**
 * Chat state + session lifecycle.
 *
 * Deliberately NOT persisted anywhere: no localStorage, no sessionStorage,
 * nothing sent to the server to rehydrate later. `sessionId` and `messages`
 * live only in this component's React state, so a page refresh or a closed
 * tab already means both are gone — the next message starts a brand-new
 * conversation from a `sessionId` the server has never seen before. The §10
 * contract is attached to each assistant message so the message list can
 * render a rich recommendation card.
 *
 * Replies stream in over Server-Sent Events (`streamChat`). Incoming text is
 * queued and revealed at a steady per-character pace rather than dumped into
 * the bubble the instant each network chunk arrives — a short reply can
 * otherwise complete in one burst and never look like it's "typing".
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, streamChat } from "../api/client";
import type { ChatMessage, RecommendationContract } from "../types";
import { redactSensitive } from "../redact";

const WELCOME = "Welcome to Alienware Arena Assistant";
// Short, and an invitation rather than a form. The desk still serves video
// editing, AI work, study and office perfectly well — see the greeting
// instruction in app/aero/graph.py — it simply does not list them before
// being asked, and the first line no longer asks the customer to classify
// themselves before they have said anything.
const INTRO = "Let's find your ideal gaming laptop";

// The greeting is TYPED, not printed. It is the first thing a customer
// sees, and having it appear fully formed while every later reply types
// itself out made the opening look like a static page rather than somebody
// talking.
const GREETING_LINES = [WELCOME, INTRO];

// One character every tick, evenly — about 40 a second.
//
// NOT the streamed reply's variable bite (up to six characters a tick when
// there is a backlog). That rule exists because the network delivers a
// reply in unpredictable bursts and a long one still has to finish in
// bounded time; a greeting is a fixed string already in hand, so the only
// thing the bite did was make the long line gallop three characters at a
// time while the short one finished in 0.7s — before the page had settled
// enough to watch it. Reported as the first line "already appearing" and
// only the last one typing.
const TYPE_TICK_MS = 25;
const TYPE_CHARS_PER_TICK = 1;
// A beat before the first character, so the opening line is typed in front
// of the customer rather than during the page's own first paint.
const GREETING_LEAD_TICKS = 10;
// And a breath between the two lines, the way a person sends one message
// and then another.
const GREETING_GAP_TICKS = 14;

/** Per-run ids, so the typing effect can address each line without having
 *  to read them back out of state. */
function greetingId(run: number, line: number): string {
  return `greet-${run}-${line}`;
}

// AERO sells the whole Dell range now (see app/aero/system_prompt.py), so
// this greeting asks what the machine is FOR rather than which game they
// play. It used to say "your gaming laptop assistant… any particular game in
// mind?", which read as a gaming-only desk to somebody who had come to buy a
// laptop for AI work — and that is the first thing they see.
// Keeping this local seed message in sync with
// that first real turn matters because it's the ONE greeting that never
// touches the backend at all (see greetings() below) — a stale seed here
// would keep showing the old 7-option menu forever, regardless of anything
// fixed server-side.
//
// No buttons under it. INTRO is an open invitation, not a closed question,
// and the chips left over from the old gaming-only greeting still said
// Yes / No — two answers that fit no question being asked. Reported
// live: the very first thing a customer sees offered them a choice between
// two words that mean nothing here. The backend applies the same rule in
// service._quick_replies_for, where `detail_name` returns None because a
// free-text answer is expected; this is the one greeting that never reaches
// it, so the rule has to be repeated here.

function uid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** The opening bubble, empty and waiting to be typed into.
 *
 *  Only the FIRST line exists to begin with — the second is appended once
 *  the first has finished — so two empty bubbles never sit there blinking
 *  at the customer. */
function greetings(run: number): ChatMessage[] {
  return [
    {
      id: greetingId(run, 0),
      role: "assistant",
      text: "",
      ts: Date.now(),
      streaming: true,
    },
  ];
}

export interface UseChat {
  messages: ChatMessage[];
  /** true from send until the first streamed fragment arrives. */
  sending: boolean;
  /** true while an assistant reply is still streaming in. */
  streaming: boolean;
  /** short "what AERO is doing" label while thinking (e.g. web-searching). */
  status: string | null;
  sessionId: string | null;
  contract: RecommendationContract | null;
  send: (text: string, opts?: { budgetInr?: number }) => void;
  reset: () => void;
}

export interface UseChatOptions {
  /** Whether the chat is actually ON SCREEN.
   *
   *  The greeting is typed, and it must be typed where the customer can
   *  watch it. ChatWidget mounts with the panel CLOSED (a launcher button
   *  on the page), but this hook has always been called from its top level
   *  — so the greeting used to type itself out invisibly the moment the
   *  PAGE loaded. Clicking the launcher a couple of seconds later revealed
   *  a finished first line with only the second still going, reported as
   *  "'Welcome to Alienware Arena Assistant' is already in the chat
   *  history and only the last line appears as typing".
   *
   *  Defaults to true, so a caller that is always visible needs to pass
   *  nothing. */
  active?: boolean;
}

export function useChat(options?: UseChatOptions): UseChat {
  const active = options?.active ?? true;
  // Bumped by reset(), which is what re-types the greeting on a fresh
  // conversation. Mirrored in a ref so reset() can build the new bubble in
  // the same tick it schedules the new run.
  const greetRunRef = useRef(0);
  const [greetRun, setGreetRun] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>(() => greetings(0));
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [contract, setContract] = useState<RecommendationContract | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const revealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Set the moment the customer sends anything, so a greeting still being
  // typed finishes instantly instead of carrying on underneath their reply
  // with a second caret blinking in the transcript.
  const spokeRef = useRef(false);
  // How far the greeting has been typed. Held in a ref so the effect can
  // RESUME rather than restart: StrictMode mounts effects twice in
  // development, and without this the opening line jumped back to the
  // beginning and retyped itself.
  const greetAtRef = useRef({ run: -1, line: 0, at: 0 });

  const stopReveal = useCallback(() => {
    if (revealTimerRef.current !== null) {
      clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  }, []);

  // Types the greeting out, one line after the other. Runs on mount and
  // again on every reset().
  //
  // Deliberately does NOT touch the `streaming` state flag — only the
  // per-message one, which is what draws the caret. `streaming` is also
  // send()'s guard, so setting it here would lock the composer for the two
  // seconds the greeting takes, and a customer who already knows what they
  // want should never be made to wait for an animation.
  useEffect(() => {
    // Nothing is typed into a panel nobody is looking at. Closing the chat
    // mid-greeting pauses it; opening again resumes from the same
    // character, because the progress lives in a ref.
    if (!active) return;
    if (greetAtRef.current.run !== greetRun) {
      greetAtRef.current = { run: greetRun, line: 0, at: 0 };
    }
    let line = greetAtRef.current.line;
    let at = greetAtRef.current.at;
    // Only on a fresh run: a StrictMode remount resumes mid-line and must
    // not sit through the lead-in a second time.
    let waiting = at === 0 && line === 0 ? GREETING_LEAD_TICKS : 0;
    const timer = setInterval(() => {
      // They have started talking. Put the greeting up in full and stop.
      if (spokeRef.current) {
        clearInterval(timer);
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const filled = prev.map((m) => {
            const i = GREETING_LINES.findIndex(
              (_t, n) => greetingId(greetRun, n) === m.id,
            );
            return i === -1
              ? m
              : { ...m, text: GREETING_LINES[i], streaming: false };
          });
          const missing = GREETING_LINES.map((text, n) => ({ text, n })).filter(
            ({ n }) => !seen.has(greetingId(greetRun, n)),
          );
          return [
            ...filled,
            ...missing.map(({ text, n }) => ({
              id: greetingId(greetRun, n),
              role: "assistant" as const,
              text,
              ts: Date.now() + n,
              streaming: false,
            })),
          ];
        });
        return;
      }
      // Hold still for the lead-in / the gap between lines.
      if (waiting > 0) {
        waiting -= 1;
        return;
      }
      const full = GREETING_LINES[line];
      at = Math.min(full.length, at + TYPE_CHARS_PER_TICK);
      const shown = full.slice(0, at);
      const done = at >= full.length;
      const id = greetingId(greetRun, line);
      const nextLine = line + 1;
      const hasNext = nextLine < GREETING_LINES.length;
      greetAtRef.current = { run: greetRun, line, at };
      setMessages((prev) => {
        const next = prev.map((m) =>
          m.id === id ? { ...m, text: shown, streaming: !done } : m,
        );
        // Guarded, so a remount that lands on a line already appended does
        // not add the bubble a second time.
        if (
          done &&
          hasNext &&
          !next.some((m) => m.id === greetingId(greetRun, nextLine))
        ) {
          next.push({
            id: greetingId(greetRun, nextLine),
            role: "assistant",
            text: "",
            ts: Date.now(),
            streaming: true,
          });
        }
        return next;
      });
      if (done) {
        if (hasNext) {
          line = nextLine;
          at = 0;
          waiting = GREETING_GAP_TICKS;
          greetAtRef.current = { run: greetRun, line, at };
        } else {
          clearInterval(timer);
        }
      }
    }, TYPE_TICK_MS);
    return () => clearInterval(timer);
  }, [greetRun, active]);

  const finalizeStreaming = useCallback(() => {
    setMessages((prev) =>
      prev.some((m) => m.streaming)
        ? prev.map((m) => (m.streaming ? { ...m, streaming: false } : m))
        : prev,
    );
    setStreaming(false);
    setSending(false);
    setStatus(null);
  }, []);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      stopReveal();
    },
    [stopReveal],
  );

  const send = useCallback(
    (text: string, opts?: { budgetInr?: number }) => {
      const typed = text.trim();
      if (!typed || sending || streaming) return;
      // Finishes a greeting still mid-type (see the effect above).
      spokeRef.current = true;
      const budgetInr = opts?.budgetInr;

      // Before the bubble is drawn and before the request goes out, so a
      // pasted card number is never shown back and never leaves the
      // browser. The backend redacts too; see frontend/src/redact.ts.
      const trimmed = redactSensitive(typed);

      const userMsg: ChatMessage = {
        id: uid(),
        role: "user",
        text: trimmed,
        ts: Date.now(),
      };
      const replyId = uid();
      setMessages((prev) => [
        ...prev,
        userMsg,
        { id: replyId, role: "assistant", text: "", ts: Date.now(), streaming: true },
      ]);
      setSending(true);
      setStreaming(true);
      setStatus(null);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      stopReveal();

      // A network delta can arrive in large bursts — the backend already
      // trims the reply to one short sentence, so a fast connection can
      // deliver the whole thing in a single chunk. That would just dump the
      // bubble at once instead of looking like it's being typed, so incoming
      // text is queued here and revealed at a steady pace instead, decoupled
      // from how the network actually delivered it.
      let pending = "";
      let revealed = "";
      let onTypingDone: (() => void) | null = null;

      const tick = () => {
        if (!pending) {
          stopReveal();
          const done = onTypingDone;
          onTypingDone = null;
          done?.();
          return;
        }
        // Roughly 50-300 characters a second: one character per tick when
        // the text is trickling in, up to six when there's a backlog, so a
        // long reply still finishes in a bounded time.
        //
        // The cap is what makes this read as TYPING. The provider does not
        // reliably stream token by token — a whole reply can arrive in one
        // or two chunks — and with an uncapped bite (backlog / 10) the
        // entire message was revealed in ~10 ticks, i.e. it simply appeared.
        const bite = Math.max(1, Math.min(6, Math.ceil(pending.length / 40)));
        revealed += pending.slice(0, bite);
        pending = pending.slice(bite);
        setMessages((prev) =>
          prev.map((m) => (m.id === replyId ? { ...m, text: revealed } : m)),
        );
      };

      // The model's own words are deliberately NOT shown as they arrive.
      //
      // This app rewrites a reply after the model has written it: an invented
      // price is taken out, a refusal is replaced with the honest one, a spec
      // block is written from the figures actually on file. So a sentence
      // shown while it streams can be retracted a moment later, and the
      // customer sees a message appear, vanish, and a different one take its
      // place. Reported more than once, and it came back every time another
      // rewrite was added -- because streaming the draft is what makes it
      // visible at all.
      //
      // The deltas are still consumed: they are what keeps the connection
      // readable, and the status that arrives alongside them is what the
      // typing indicator shows ("Searching the web..."). Only the FINAL,
      // authoritative reply is ever typed into the bubble, below.
      const appendDelta = (_fragment: string) => {};

      void (async () => {
        try {
          const res = await streamChat({
            message: trimmed,
            sessionId,
            budgetInr,
            signal: controller.signal,
            onDelta: appendDelta,
            onStatus: (text) => setStatus(text),
          });
          setSessionId(res.session_id);
          setContract(res.contract);

          const applyFinal = () => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === replyId
                  ? {
                      ...m,
                      text: res.reply || revealed || m.text,
                      contract: res.contract,
                      budgetPrompt: res.budget_prompt ?? null,
                      quickReplies: res.quick_replies ?? null,
                      streaming: false,
                    }
                  : m,
              ),
            );
            setStreaming(false);
            setSending(false);
            setStatus(null);
          };
          // Type the final reply out HERE, rather than swapping it in over
          // whatever streamed (see appendDelta). The card and the quick
          // replies go on at the end of the animation, so the bubble is
          // never shown one thing and then another.
          if (res.reply) {
            pending = res.reply;
            revealed = "";
            onTypingDone = applyFinal;
            setSending(false);
            setStatus(null);
            if (revealTimerRef.current === null) {
              revealTimerRef.current = setInterval(tick, 20);
            }
          } else {
            applyFinal();
          }
        } catch (err) {
          stopReveal();
          if (err instanceof DOMException && err.name === "AbortError") return;
          const message =
            err instanceof ApiError ? err.message : "Unexpected error. Please try again.";
          setMessages((prev) => {
            // Drop the placeholder if nothing streamed; otherwise keep the
            // partial reply (no longer streaming) and add the error below it.
            const kept = prev
              .filter((m) => m.id !== replyId || m.text !== "")
              .map((m) => (m.id === replyId ? { ...m, streaming: false } : m));
            return [
              ...kept,
              { id: uid(), role: "error", text: message, ts: Date.now() },
            ];
          });
          setStreaming(false);
          setSending(false);
          setStatus(null);
        }
      })();
    },
    [sending, streaming, sessionId, stopReveal],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    stopReveal();
    finalizeStreaming();
    setSessionId(null);
    setContract(null);
    spokeRef.current = false;
    greetRunRef.current += 1;
    setGreetRun(greetRunRef.current);
    setMessages(greetings(greetRunRef.current));
  }, [finalizeStreaming, stopReveal]);

  return { messages, sending, streaming, status, sessionId, contract, send, reset };
}
